import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { ROLE_LABELS } from '../types/auth';
import { ApiError } from '../api/client';

/**
 * Which program (account) is active, and — for an Organizer/Head Coach who hasn't
 * started one yet — the means to start it.
 *
 * <p>One organizer runs at most one program (see {@code AccountService#createAccount}),
 * so "Start a program" disappears once {@code user.accounts} already contains one they
 * own; editing that program's own details is a separate, still-open affordance, not
 * blocked by this. With exactly one reachable account there's nothing to switch
 * between, so it renders as a plain title rather than a single-option dropdown — the
 * dropdown only earns its place once there's a second account to pick from (recording
 * for someone else's program as a contributor, say). Shared between the Dashboard,
 * where it decides what data is shown, and the Profile page, where it's just another
 * piece of the signed-in person's own settings — both read and act on the same
 * active-account state in {@link useAuth}.
 *
 * <p>Starting a program itself is free and instant — the person already selected and
 * paid for the Organizer/Head Coach role at registration (see {@code
 * RoleSelectionModal}); this only requires that they hold it, same as the server does.
 */
export function ProgramSwitcher() {
  const { user, activeAccountId, activeAccount, setActiveAccountId, canWrite, startAccount } =
    useAuth();

  const [showStartAccount, setShowStartAccount] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [startAccountBusy, setStartAccountBusy] = useState(false);
  const [startAccountError, setStartAccountError] = useState('');

  async function handleStartAccount(e: React.FormEvent) {
    e.preventDefault();
    if (!newAccountName.trim()) return;

    setStartAccountBusy(true);
    setStartAccountError('');
    try {
      await startAccount(newAccountName.trim());
      setNewAccountName('');
      setShowStartAccount(false);
    } catch (caught) {
      setStartAccountError(
        caught instanceof ApiError ? caught.message : 'Could not start that program.'
      );
    } finally {
      setStartAccountBusy(false);
    }
  }

  // Only an Organizer/Head Coach can start one at all, and only once — system admins
  // manage other people's accounts rather than their own program, and anyone who
  // already organizes one has used up their one, per the server-side rule.
  const alreadyOrganizes = (user?.accounts ?? []).some(a => a.role === 'owner');
  const canStartAccount = !user?.systemAdmin && user?.role === 'owner' && !alreadyOrganizes;

  if (!user) return null;
  if (user.accounts.length === 0 && !canStartAccount) return null;

  return (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-4">
      {user.accounts.length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-sm text-blue-300">Program</label>
          <select
            value={activeAccountId ?? ''}
            onChange={e => setActiveAccountId(e.target.value)}
            className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
          >
            {user.accounts.map(account => (
              <option
                key={account.accountId}
                value={account.accountId}
                className="bg-blue-950 text-white"
              >
                {account.accountName}
              </option>
            ))}
          </select>
        </div>
      )}

      {user.accounts.length === 1 && (
        <div className="flex items-center gap-3">
          <span className="text-sm text-blue-300">Program</span>
          <span className="text-white font-medium">{user.accounts[0].accountName}</span>
        </div>
      )}

      {user.accounts.length > 0 && (
        <>
          {activeAccount && (
            <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-200">
              {ROLE_LABELS[activeAccount.role]}
            </span>
          )}
          {!canWrite && (
            <span className="text-xs text-blue-300/70">
              Read-only here — you can review games but not record them.
            </span>
          )}
        </>
      )}

      {canStartAccount && (showStartAccount ? (
        <form onSubmit={handleStartAccount} className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={newAccountName}
            onChange={e => { setNewAccountName(e.target.value); setStartAccountError(''); }}
            placeholder="Program or team name"
            autoFocus
            className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/60"
          />
          <button
            type="submit"
            disabled={startAccountBusy || !newAccountName.trim()}
            className="bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm transition-colors"
          >
            {startAccountBusy ? 'Starting…' : 'Start'}
          </button>
          <button
            type="button"
            onClick={() => { setShowStartAccount(false); setStartAccountError(''); }}
            className="text-blue-300/70 hover:text-blue-200 text-sm px-2"
          >
            Cancel
          </button>
          {startAccountError && (
            <span className="text-red-400 text-xs w-full">{startAccountError}</span>
          )}
        </form>
      ) : (
        <button
          onClick={() => setShowStartAccount(true)}
          className="flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200 transition-colors ml-auto"
        >
          <Plus className="w-4 h-4" />
          Start a program
        </button>
      ))}
    </div>
  );
}
